import {
  User,
  UserManager,
  UserManagerSettings,
} from 'oidc-client';
import {
  Injectable,
  signal,
  WritableSignal,
  inject,
} from '@angular/core';
import {
  from,
  Observable,
  Subject,
} from 'rxjs';
import {environment} from '../../../../../../environments/environment';
import {JwtService} from '../jwt/jwt.service';
import {AppTranslateService} from '../../page/i18n/translate/app-translate/app-translate.service';
import {Links} from '../../../../common/constants/links';
import {DeputyContextService} from '../deputy-context/deputy-context.service';

export {User};

@Injectable({
  providedIn: 'root',
})
export class OidcAuthService {

  private jwtService = inject(JwtService);

  private translate = inject(AppTranslateService);

  private deputyContextService = inject(DeputyContextService);

  private userManager: UserManager;

  public isStartedLoginProcess: WritableSignal<boolean> = signal<boolean>(false);

  public updateLangAfterRenewTokenSubject: Subject<any> = new Subject<any>();

  constructor() {
    const settings: UserManagerSettings = {
      authority: environment.oidcSettings.authority,
      client_id: environment.oidcSettings.client_id,
      // not required, page to finish authorisation process and redirect back to required page
      redirect_uri: environment.oidcSettings.redirect_uri,
      response_type: environment.oidcSettings.response_type,
      scope: environment.oidcSettings.scope,
      prompt: environment.oidcSettings.prompt, // allow not to ask to Approve login
      loadUserInfo: false,
      revokeAccessTokenOnSignout: true,
      // silent renew
      includeIdTokenInSilentRenew: true,
      // Flag to indicate if there should be an automatic attempt to renew the access token prior to its expiration.
      // The attempt is made as a result of the accessTokenExpiring event being raised.
      automaticSilentRenew: true,
      // accessTokenExpiringNotificationTime: 240,

      metadata: {
        authorization_endpoint: environment.oidcSettings.metadata.authorization_endpoint,
        token_endpoint: environment.oidcSettings.metadata.token_endpoint,
        end_session_endpoint: environment.oidcSettings.metadata.end_session_endpoint,
        revocation_endpoint: environment.oidcSettings.metadata.revocation_endpoint,
      },
    };

    this.userManager = new UserManager(settings);

    this.userManager.events.addUserLoaded(user => {
      const changedLang = this.jwtService.getLang() !== this.jwtService.decodeToken(user.access_token).account?.lang;
      if (changedLang) {
        this.updateLangAfterRenewTokenSubject.next(true);
      }
      this.jwtService.setToken(user);
      this.translate.initLang();
    });
  }

  /**
   * Load the User object for the currently authenticated user
   */
  public getUser(): Promise<User> {
    return this.userManager.getUser();
  }

  /**
   * Load the User object for the currently authenticated user use Observable
   */
  public getUserObservable(): Observable<User> {
    return from(this.userManager.getUser());
  }

  /**
   * Trigger a redirect of the current window to the authorization endpoint
   */
  public login(): Promise<void> {
    if (this.isStartedLoginProcess()) {
      return Promise.resolve();
    }

    this.isStartedLoginProcess.set(true);
    const params: any = {state: window.location.href};
    return this.userManager.signinRedirect(params);
  }

  /**
   * Process response from the authorization endpoint. A fresh redirect login
   * starts a new session, so any leftover deputy context from a
   * previous session on this browser is cleared. This does NOT fire on a plain
   * page reload or silent token renew, so F5 keeps the context.
   */
  public signinRedirectCallback(): Promise<User> {
    this.deputyContextService.clearDeputyContext();
    return this.userManager.signinRedirectCallback();
  }

  /**
   * Trigger a silent request (via an iframe or refresh token if available) to the authorization endpoint
   */
  public renewToken(): Promise<User> {
    return this.userManager.signinSilent();
  }

  /**
   * Trigger a silent request (via an iframe or refresh token if available) to the authorization endpoint
   */
  public renewTokenCallback(): Promise<User> {
    return this.userManager.signinSilentCallback();
  }

  /**
   * Trigger a redirect of the current window to the end session endpoint
   */
  public logout(): Promise<void> {
    this.deputyContextService.clearDeputyContext();
    return this.userManager.signoutRedirect();
  }

  /**
   * Method: Redirect after login
   * @param user
   */
  private redirectAfterLogin(user?: User): string {
    const state = user?.state ? new URL(user.state) : null;
    const redirect = state ? state.pathname : Links.LIBRARY_LINK_URL;
    const getParams = state ? state.search : '';
    return redirect + getParams;
  }

  /**
   * Method: Check url for auth
   */
  public isAuthProcess(): boolean {
    return ['code', 'state'].some(item => window.location.href.includes(item)) && !!window.location.search;
  }

  /**
   * Method: Auth user in application
   */
  public async authUserInApp(): Promise<string> {
    const user = await this.signinRedirectCallback();
    return this.redirectAfterLogin(user);
  }

  /**
   * Method: Check current user
   * @param currentUser - current user
   */
  public async checkAuthUser(currentUser: any): Promise<void> {
    if (!currentUser) {
      await this.login();
    }
  }

}
